/**
 * Turning an API error body into flat, displayable strings. A handled API error answers with a DRF
 * JSON body (`{detail}`, `{non_field_errors}`, or a field → messages map). An *unhandled* one — a 500
 * with `DEBUG=False`, a gateway fault, an auth proxy redirect — answers with an HTML page instead.
 */

/** Longest single message shown before truncation. */
const MAX_MESSAGE_LENGTH = 300

/** Most messages shown at once; a well-formed body never comes close. */
const MAX_MESSAGES = 10

export const GENERIC_API_ERROR =
  'Something went wrong on our end. Please try again, and contact support if the problem continues.'

/** Keys whose messages stand alone — prefixing them reads as a field name. */
const UNPREFIXED_KEYS = ['detail', 'non_field_errors', 'error', 'message']

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** True when the body is a markup document rather than an API message. */
export function isHtmlErrorBody(data: unknown): boolean {
  if (typeof data !== 'string') {
    return false
  }
  const head = data.trim().slice(0, 200).toLowerCase()

  return head.startsWith('<!doctype') || head.includes('<html')
}

function truncate(value: string): string {
  const collapsed = value.replace(/\s+/g, ' ').trim()

  return collapsed.length > MAX_MESSAGE_LENGTH
    ? `${collapsed.slice(0, MAX_MESSAGE_LENGTH - 1)}…`
    : collapsed
}

/** `non_field_errors` → `Non field errors`; left alone if already spaced. */
function humanizeKey(key: string): string {
  const spaced = key.replace(/_/g, ' ').trim()

  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Reduce one field's value — string, number, list, or nested map — to a line. */
function flattenValue(value: unknown): string {
  if (typeof value === 'string') {
    return truncate(value)
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  if (Array.isArray(value)) {
    return value.map(flattenValue).filter(Boolean).join(' ')
  }
  if (isPlainObject(value)) {
    return toErrorList(value, '').filter(Boolean).join(' ')
  }

  return ''
}

/**
 * Flatten an error body to display strings, falling back to a single generic
 * message for anything that is not a recognizable API error shape.
 */
export function toErrorList(
  data: unknown,
  fallback: string = GENERIC_API_ERROR
): string[] {
  const fallbackList = fallback ? [fallback] : []

  // An HTML page carries no message worth showing a merchant.
  if (data == null || isHtmlErrorBody(data)) {
    return fallbackList
  }

  if (typeof data === 'string') {
    const message = truncate(data)

    return message ? [message] : fallbackList
  }

  if (!isPlainObject(data) && !Array.isArray(data)) {
    return fallbackList
  }

  if (Array.isArray(data)) {
    const messages = data.map(flattenValue).filter(Boolean)

    return messages.length ? messages.slice(0, MAX_MESSAGES) : fallbackList
  }

  // `detail` is DRF's single-message form; when present it is the whole error.
  if (typeof data.detail === 'string' && data.detail.trim()) {
    return [truncate(data.detail)]
  }

  const messages = Object.entries(data)
    .map(([key, value]) => {
      const message = flattenValue(value)
      if (!message) {
        return ''
      }

      return UNPREFIXED_KEYS.includes(key)
        ? message
        : `${humanizeKey(key)}: ${message}`
    })
    .filter(Boolean)

  return messages.length ? messages.slice(0, MAX_MESSAGES) : fallbackList
}
